import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { accessDeniedUrlTree } from '@overa/security';
import { map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { PlatformCapabilitiesService } from './platform-capabilities.service';
import { PlatformService } from './platform.service';
import { sessionPlatform } from './platform-session';

/** Segmento de la ruta que lleva el código de plataforma: `/p/{platform}/...`. */
export const PLATFORM_PARAM = 'platform';

/**
 * Fija la plataforma activa a partir de la URL, y solo a partir de la URL.
 *
 * Es el único sitio que llama a `setActive`. Así la dirección es siempre la verdad sobre qué se está
 * administrando: si alguien pega un enlace de otra plataforma, el ámbito cambia con él, y si recarga,
 * sigue donde estaba. Un ámbito que vive solo en memoria se pierde al recargar y —peor— uno que vive
 * solo en el almacenamiento reaparece sin que nadie lo haya elegido.
 *
 * Una plataforma desconocida o no cableada NO se abre a medias: se devuelve al lanzador, donde se ve
 * por qué no está disponible. Entrar y que vayan fallando las pantallas una a una sería peor.
 *
 * Las que la persona no alcanza —no tiene ninguna ficha de usuario en ningún inquilino de ese
 * producto— van en cambio a acceso denegado: eso sí es una cuestión de permisos, y se comprueba aquí y
 * no solo al pintar el lanzador porque esconder la tarjeta y dejar entrar escribiendo la dirección
 * sería un filtro de adorno. Aun así **no es la puerta**: la puerta es el API de cada producto. Ver
 * `PlatformService.isReachable`.
 */
export const platformGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const platforms = inject(PlatformService);
  const capabilities = inject(PlatformCapabilitiesService);
  const router = inject(Router);

  const code = route.paramMap.get(PLATFORM_PARAM);
  if (!code) return of(router.parseUrl('/launcher'));

  // Una sesión de plataforma solo abre la suya: su token no vale en ninguna otra.
  const own = sessionPlatform();
  if (own && own !== code) return of(router.parseUrl(`/p/${encodeURIComponent(own)}`));

  return platforms.load().pipe(
    switchMap((): Observable<boolean | UrlTree> => {
      const platform = platforms.find(code);
      if (!platform?.isAvailable) {
        platforms.setActive(null);
        return of(router.parseUrl('/launcher'));
      }
      if (!platforms.isReachable(code)) {
        platforms.setActive(null);
        return of(accessDeniedUrlTree(router, state.url));
      }

      platforms.setActive(platform);
      // Not awaited: the menu hides unsupported entries when the probe answers, and a slow destination
      // must not hold the navigation.
      capabilities.load(platform.code).subscribe();
      // The platform's tenant has to be known before the screen fires its first request, or that request
      // would reach the platform with the operator's console tenant.
      return platforms.loadTenants(platform.code).pipe(map(() => true));
    }),
  );
};

/**
 * Deja el primer nivel sin plataforma activa: lanzador y catálogo están por encima de cualquier
 * plataforma, y dejar la anterior marcada haría que el menú siguiera enseñando su sección.
 */
export const noPlatformGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  // La consola (lanzador, catálogo, operadores, IA global) no existe para una sesión de plataforma.
  const own = sessionPlatform();
  if (own) return of(inject(Router).parseUrl(`/p/${encodeURIComponent(own)}`));

  const platforms = inject(PlatformService);
  platforms.setActive(null);
  return platforms.load().pipe(map(() => true));
};
